/**
 * Invoice Management Configuration Helper
 * Provides pre-configured settings for different module types
 */

// Single unified configuration for all shipping line payment components
export const InvoiceConfig = {
  slineType: 'FFSline',
  transportType: 'Export',
  documentName: 'SlinePay_SlineInv_',
  otherDocName: 'SlinePay_SlineInv_',
  invCategoryList: [
    {
      payVal: "Supplementary",
      payText: "Supplementary",
      notEnabled: true,
    },
    {
      payVal: "Normal",
      payText: "Normal",
      notEnabled: true,
    },
  ],
  invoiceTypeList: [
    { value: "1", text: "OCEAN FREIGHT" },
    { value: "2", text: "DETENTION CHARGES" },
    { value: "3", text: "SURRENDER CHARGES" },
    { value: "4", text: "LDC CHARGES" },
    { value: "5", text: "LOCAL CHARGES" },
    { value: "6", text: "SEAL CHARGES" },
    { value: "7", text: "VIA CHARGES" },
    { value: "8", text: "STORAGE CHARGES" },
    { value: "9", text: "ROLL-ON CHARGES" },
    { value: "10", text: "TERMINAL HANDLING CHARGES (THC)" },
    { value: "11", text: "SEAWAY BL CHARGES" },
    { value: "12", text: "ORIGINAL BL CHARGES" },
    { value: "13", text: "B/L AMENDMENT CHARGES" },
    { value: "14", text: "DESTINATION CHARGES" },
    { value: "15", text: "CREDIT NOTE" },
    { value: "16", text: "BOOKING CANCELLATION" },
  ],
  scCategoryList: [
    { SCVal: "Yes", SCText: "Yes" },
    { SCVal: "No", SCText: "No" },
  ],
  currList: [
    { payVal: "INR", payText: "INR" },
    { payVal: "USD", payText: "USD" },
  ],
  payList: [
    { payVal: "1", payText: "RTGS" },
    { payVal: "2", payText: "Cheque" },
  ],
  statusList: [
    { payVal: "2", payText: "COMPLETED" },
  ],
  columnSizes: {
    category: 3,
    number: 3,
    type: 6
  }
};

/**
 * Get the unified invoice configuration
 * @returns {Object} Configuration object
 */
export function getInvoiceConfig() {
  return InvoiceConfig;
}

/**
 * Create a custom configuration by merging with the base configuration
 * @param {Object} customConfig - Custom configuration overrides
 * @returns {Object} Merged configuration object
 */
export function createCustomInvoiceConfig(customConfig) {
  // Use Object.assign for compatibility with older Babel versions
  const mergedConfig = Object.assign({}, InvoiceConfig, customConfig);
  
  // Deep merge for nested objects
  if (customConfig.columnSizes) {
    mergedConfig.columnSizes = Object.assign({}, InvoiceConfig.columnSizes, customConfig.columnSizes);
  }
  
  return mergedConfig;
}

/**
 * Get configuration for any shipping line payment component
 * @param {string} fileName - Component file name for specific configuration
 * @returns {Object} Configuration object
 */
export function getConfigByFileName(fileName) {
  const configMap = {
    // Normal Invoice Configurations
    'ccSLinePaymentExportN.vue': 'CC_EXPORT',
    'ccSLinePaymentImportN.vue': 'CC_IMPORT',
    'ffSLinePaymentExportN.vue': 'FF_EXPORT',
    'ffSLinePaymentExportN_VP.vue': 'FF_EXPORT',
    'ffSLinePaymentImportN.vue': 'FF_IMPORT',
    'miscInvoiceSlinePayment_VP.vue': 'MISC_INVOICE',
    'ffConSLinePaymentExport_VP.vue': 'FF_CON_EXPORT',
    
    // Supplementary Invoice Configurations
    'ccSLinePaymentExportN_Supplementary.vue': 'CC_EXPORT_SUPPLEMENTARY',
    'ccSLinePaymentImportN_Supplementary.vue': 'CC_IMPORT_SUPPLEMENTARY',
    'ffSLinePaymentExportN_Supplementary.vue': 'FF_EXPORT_SUPPLEMENTARY',
    'ffSLinePaymentExportN_VP_Supplementary.vue': 'FF_EXPORT_SUPPLEMENTARY',
    'ffSLinePaymentImportN_Supplementary.vue': 'FF_IMPORT_SUPPLEMENTARY',
    'miscInvoiceSlinePayment_VP_Supplementary.vue': 'MISC_INVOICE_SUPPLEMENTARY',
    'ffConSLinePaymentExport_VP_Supplementary.vue': 'FF_CON_EXPORT_SUPPLEMENTARY',
    'ffConSLineSuplimentryPayment_VP.vue': 'FF_CON_SUPPLEMENTARY'
  };
  
  const configKey = configMap[fileName];
  if (configKey) {
    return getSpecificConfig(configKey);
  }
  
  // Default fallback
  return InvoiceConfig;
}

/**
 * Get specific configuration based on module type
 * @param {string} moduleType - Module type (CC_EXPORT, CC_IMPORT, FF_EXPORT, FF_IMPORT, etc.)
 * @returns {Object} Configuration object with specific document names
 */
function getSpecificConfig(moduleType) {
  const baseConfig = Object.assign({}, InvoiceConfig);
  
  // Override document names based on module type
  switch (moduleType) {
    case 'CC_EXPORT':
      baseConfig.documentName = 'CC_Export_SlinePay_SlineInv_';
      baseConfig.otherDocName = 'CC_Export_SlinePay_SlineInv_';
      baseConfig.slineType = 'CCSline';
      break;
    case 'CC_EXPORT_SUPPLEMENTARY':
      baseConfig.documentName = 'CC_Export_Supplementary_SlinePay_SlineInv_';
      baseConfig.otherDocName = 'CC_Export_Supplementary_SlinePay_SlineInv_';
      baseConfig.slineType = 'CCSline';
      break;
    case 'CC_IMPORT':
      baseConfig.documentName = 'CC_Import_SlinePay_SlineInv_';
      baseConfig.otherDocName = 'CC_Import_SlinePay_SlineInv_';
      baseConfig.slineType = 'Sline';
      break;
    case 'CC_IMPORT_SUPPLEMENTARY':
      baseConfig.documentName = 'CC_Import_Supplementary_SlinePay_SlineInv_';
      baseConfig.otherDocName = 'CC_Import_Supplementary_SlinePay_SlineInv_';
      baseConfig.slineType = 'Sline';
      break;
    case 'FF_EXPORT':
    case 'FF_CON_EXPORT':
      baseConfig.documentName = 'Sea_Export_SlinePay_SlineInv_';
      baseConfig.otherDocName = 'Sea_Export_SlinePay_SlineInv_';
      baseConfig.slineType = 'FFSline';
      break;
    case 'FF_EXPORT_SUPPLEMENTARY':
    case 'FF_CON_EXPORT_SUPPLEMENTARY':
      baseConfig.documentName = 'Sea_Export_Supplementary_SlinePay_SlineInv_';
      baseConfig.otherDocName = 'Sea_Export_Supplementary_SlinePay_SlineInv_';
      baseConfig.slineType = 'FFSline';
      break;
    case 'FF_IMPORT':
      baseConfig.documentName = 'Sea_Import_SlinePay_SlineInv_';
      baseConfig.otherDocName = 'Sea_Import_SlinePay_SlineInv_';
      baseConfig.slineType = 'Sline';
      break;
    case 'FF_IMPORT_SUPPLEMENTARY':
      baseConfig.documentName = 'Sea_Import_Supplementary_SlinePay_SlineInv_';
      baseConfig.otherDocName = 'Sea_Import_Supplementary_SlinePay_SlineInv_';
      baseConfig.slineType = 'Sline';
      break;
    case 'FF_CON_SUPPLEMENTARY':
      baseConfig.documentName = 'suplimentrySlineInvoive_';
      baseConfig.otherDocName = 'suplimentrySlineInvoive_';
      baseConfig.slineType = 'FFSline';
      break;
    case 'MISC_INVOICE':
      baseConfig.documentName = 'MISC_Invoice_SlinePay_SlineInv_';
      baseConfig.otherDocName = 'MISC_Invoice_SlinePay_SlineInv_';
      baseConfig.slineType = 'FFSline';
      break;
    case 'MISC_INVOICE_SUPPLEMENTARY':
      baseConfig.documentName = 'MISC_Invoice_Supplementary_SlinePay_SlineInv_';
      baseConfig.otherDocName = 'MISC_Invoice_Supplementary_SlinePay_SlineInv_';
      baseConfig.slineType = 'FFSline';
      break;
  }
  
  return baseConfig;
}

/**
 * Get configuration by module type and invoice category
 * @param {string} moduleType - Module type (CC_EXPORT, CC_IMPORT, FF_EXPORT, FF_IMPORT, etc.)
 * @param {string} invoiceCategory - Invoice category ('Normal' or 'Supplementary')
 * @returns {Object} Configuration object
 */
export function getConfigByModuleType(moduleType, invoiceCategory = 'Normal') {
  const configKey = invoiceCategory === 'Supplementary' 
    ? `${moduleType}_SUPPLEMENTARY` 
    : moduleType;
  
  return getSpecificConfig(configKey);
}

/**
 * Get all available module types with their normal and supplementary variants
 * @returns {Array} Array of module type objects
 */
export function getAllModuleTypes() {
  return [
    { type: 'CC_EXPORT', name: 'CC Export', hasSupplementary: true },
    { type: 'CC_IMPORT', name: 'CC Import', hasSupplementary: true },
    { type: 'FF_EXPORT', name: 'FF Export', hasSupplementary: true },
    { type: 'FF_IMPORT', name: 'FF Import', hasSupplementary: true },
    { type: 'FF_CON_EXPORT', name: 'FF Container Export', hasSupplementary: true },
    { type: 'MISC_INVOICE', name: 'MISC Invoice', hasSupplementary: true }
  ];
}

/**
 * Validate invoice configuration
 * @param {Object} config - Configuration to validate (optional, defaults to main config)
 * @returns {Object} Validation result with isValid boolean and errors array
 */
export function validateInvoiceConfig(config) {
  config = config || InvoiceConfig;
  const errors = [];
  
  if (!config.slineType) {
    errors.push('slineType is required');
  }
  
  if (!config.transportType) {
    errors.push('transportType is required');
  }
  
  if (!config.documentName) {
    errors.push('documentName is required');
  }
  
  if (!config.invCategoryList || config.invCategoryList.length === 0) {
    errors.push('invCategoryList is required and must not be empty');
  }
  
  if (!config.invoiceTypeList || config.invoiceTypeList.length === 0) {
    errors.push('invoiceTypeList is required and must not be empty');
  }
  
  if (!config.currList || config.currList.length === 0) {
    errors.push('currList is required and must not be empty');
  }
  
  if (!config.payList || config.payList.length === 0) {
    errors.push('payList is required and must not be empty');
  }
  
  if (!config.statusList || config.statusList.length === 0) {
    errors.push('statusList is required and must not be empty');
  }
  
  return {
    isValid: errors.length === 0,
    errors: errors
  };
}


// WEBPACK FOOTER //
// ./src/helper/InvoiceConfigHelper.js